import React, { useState, useEffect  } from 'react';
import './ScheduleTable.css';
import config from '../../config';
import axios from 'axios'; 
import calendarIcon from '../../image/icons/day.png'; // 만약 svg가 있다면
import clockIcon from '../../image/icons/clock.png'; // 공백 제거한 이름 추천
import lightningIcon from '../../image/icons/lightning.png';
import useIsMobile from '../../hooks/useIsMobile';

// 오전 8:00 ~ 오후 7:00 (정각 기준, 총 12개)
const timeSlots = Array.from({ length: 12 }, (_, i) => {
  const hour = i + 8; // 8 ~ 19
  return `${hour.toString().padStart(2, '0')}:00`;
});

const days = ['월요일', '화요일', '수요일', '목요일', '금요일', '토요일', '일요일'];

export default function ScheduleTable() {
  const [schedule, setSchedule] = useState(() =>
    Object.fromEntries(days.map(day => [day, Array(timeSlots.length - 1).fill(null)]))
  );

  const isMobile = useIsMobile();

  const [selectedDay, setSelectedDay] = useState('월요일');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('19:00');
  const [powerLevel, setPowerLevel] = useState('100');

  // 1시간 단위 인덱스 계산
  const getTimeIndex = (timeStr) => {
    const [h] = timeStr.split(':');
    const hour = parseInt(h, 10);
    return hour - 8;  // 8시 → 0, 9시 → 1, ..., 18시 → 10
  };

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await axios.get(`${config.backendUrl}/api/load-schedule`);
        const data = res.data;


        // 초기 상태 복사
        const updated = Object.fromEntries(days.map(day => [day, Array(timeSlots.length - 1).fill(null)]));

        // 받아온 데이터를 테이블 형식에 맞게 세팅
        data.forEach(item => {
          const { day, time_slot, power_level } = item;
          const index = getTimeIndex(time_slot);
          if (updated[day] && index >= 0 && index < updated[day].length) {
            updated[day][index] = power_level.toString(); // 상태는 문자열로 저장되어 있음
          }
        });

        setSchedule(updated);
      } catch (error) {
        console.error('스케줄 불러오기 실패:', error);
      }
    };

    fetchSchedule();
  }, []);
  
  
  const handleApply = async () => {
    const startIdx = getTimeIndex(startTime);
    const endIdx = getTimeIndex(endTime);
    const updated = { ...schedule };

    if (startIdx >= endIdx) {
      alert('종료 시간은 시작 시간보다 이후여야 합니다.');
      return;
    }

    updated[selectedDay] = updated[selectedDay].map((val, i) => {
      // 선택된 구간만 업데이트
      if (i >= startIdx && i < endIdx) {
        return powerLevel;
      }
      // 나머지 구간은 기존 값 유지
      return val;
    });

    setSchedule(updated);
  };

  const SaveSchedule = async () => {
    try {
      const payload = [];

      days.forEach((day) => {
        schedule[day].forEach((power_level, index) => {
          if (power_level !== null) {
            const time_slot = timeSlots[index]; // 08:00 ~ 18:00
            payload.push({
              day,
              time_slot,
              power_level: parseInt(power_level),
            });
          }
        });
      });

      if (payload.length === 0) {
        alert('저장할 데이터가 없습니다.');
        return;
      }

      await axios.post(`${config.backendUrl}/api/save-schedule`, payload);
      alert('전체 스케줄이 저장되었습니다');
    } catch (error) {
      console.error('백엔드 전송 실패:', error);
      alert('서버 전송에 실패했습니다');
    }
  };


  const getDominantValue = (val) => {
    const parse = (v) => parseInt(v?.replace('%', '') || '0', 10);
    return parse(val) ? val : '';
  };

  const getColor = (val) => {
    if (!val) return '#f5f6f7'; // 기본 매우 연한 그레이

    const p = parseInt(val);
    if (p >= 100) return '#f3f1f0'; // 그레이+블루
    if (p >= 90)  return '#dcdedf'; // 흐린 스톤
    if (p >= 80)  return '#e1e3dd'; // 흐린 올리브
    if (p >= 70)  return '#e7e7db'; // 흐린 샌드
    if (p >= 60)  return '#eceae2'; // 그레이+아이보리
    if (p >= 50)  return '#efede7'; // 웜 그레이
    if (p >= 40)  return '#f1efed'; // 흐린 모브
    if (p >= 30)  return '#d7dfe5'; // 라이트 펄
    if (p >= 20)  return '#f5f4f3'; // 흐린 베이지
    if (p >= 10)  return '#f7f6f5'; // 거의 흰 회색
    return '#fafafa';              // 기본 흰 회색
  };

  const getTextColor = (val) => {
    const p = parseInt(val);
    if (!val || p < 10) return '#555'; // 중간 회색
    return '#4d4d4d'; // 채도 낮은 다크 그레이 (가독성 높임)
  };

  const handleClearSchedule = async () => {
    const confirmClear = window.confirm('정말 전체 스케줄을 삭제하시겠습니까?');
    if (!confirmClear) return;

    // 1. 프론트 스케줄 초기화
    const cleared = Object.fromEntries(days.map(day => [day, Array(timeSlots.length - 1).fill(null)]));
    setSchedule(cleared);

    try {
      // 2. 백엔드에 삭제 요청 (DELETE 메서드 또는 별도 API)
      await axios.delete(`${config.backendUrl}/api/delete-schedule`);
      alert('전체 스케줄이 삭제되었습니다.');
    } catch (error) {
      console.error('스케줄 삭제 실패:', error);
      alert('서버에서 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="schedule-container">
      <div className="schedule-layout" style={{}}>
        {/* 오른쪽: 테이블 영역 */}
        <div className="schedule-table-wrapper">
          <table className="schedule-table">
            <thead>
              <tr>
                <th>시간</th>
                {days.map((d) => (
                  <th key={d}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: timeSlots.length - 1 }).map((_, rowIndex) => {
                const slotLabel = `${timeSlots[rowIndex]} ~ ${timeSlots[rowIndex + 1]}`;
                return (
                  <tr key={rowIndex}>
                    <td>{slotLabel}</td>
                    {days.map((day) => {
                      const val = schedule[day][rowIndex];
                      const display = getDominantValue(val);
                      return (
                        <td
                          key={day}
                          className={display ? '' : 'empty'}
                          style={{
                            backgroundColor: getColor(display),
                            color: getTextColor(display), // 글자색 추가
                          }}
                        >
                          {display ? `${display}%` : ''}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* 왼쪽: 설정 영역 */}
        <div className="schedule-controls">
          <div className='inverterGenInfo-TopBox'>
            <div style={{ color: '#757373', fontSize: isMobile ? '14px' : '20px', fontWeight: '800', paddingLeft: '20px' }}>◎ 발전 스케줄 설정 </div>
            <div style={{ flexGrow: '1' }}></div>
            <div style={{ color: '#757373', fontSize: isMobile ? '13px' : '19px', fontWeight: '800', paddingRight: '20px' }}>⦁ ⦁ ⦁</div>
          </div>
          <div className='schedule-controls-bottombox'>
            <div className="schedule-controls-rightbox">
              <div className="form-row">
                <label className="form-label">요일 선택</label>
                <select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)}>
                  {days.map((d) => (
                    <option key={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label className="form-label">시간 선택</label>
                <div className="time-range">
                  <select value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                    {timeSlots.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                  <span className="tilde">~</span>
                  <select value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                    {timeSlots.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <label className="form-label">발전량 선택</label>
                <select value={powerLevel} onChange={(e) => setPowerLevel(e.target.value)}>
                  {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((val) => (
                    <option key={val} value={val}>{val}%</option>
                  ))}
                </select>
              </div>
              <div>
                <button className="button button-confirm" onClick={handleApply}>확인</button>
                <div className="setting-btn">
                  <button className="button button-clear" onClick={handleClearSchedule}>전체삭제</button>
                  <button className="button button-save" onClick={SaveSchedule}>저장하기</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

}
